/**
 * Audio utilities for handling Google Cloud Storage URLs and audio processing
 */

/**
 * Generate a Google Cloud Storage URL for an audio file
 * @param audioFileKey - The file key/path in the storage bucket
 * @param bucketName - The Google Cloud Storage bucket name (optional, uses env config)
 * @returns The full URL to the audio file
 */
export function generateGcsAudioUrl(
  audioFileKey: string,
  bucketName?: string
): string {
  if (!audioFileKey) return '';

  // Use environment-specific bucket name or fallback
  const bucket =
    bucketName || import.meta.env.VITE_GCS_AUDIO_BUCKET || 'ai-scribe-staging';

  return `https://storage.googleapis.com/${bucket}/${audioFileKey}`;
}

/**
 * Generate a proxy URL to avoid CORS issues
 * @param audioFileKey - The file key/path in the storage bucket
 * @returns The proxy URL for the audio file
 */
export function generateProxyAudioUrl(audioFileKey: string): string {
  if (!audioFileKey) return '';

  // This would be your backend endpoint that proxies GCS requests
  const backendUrl =
    import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';
  return `${backendUrl}/api/audio/proxy?file=${encodeURIComponent(
    audioFileKey
  )}`;
}

/**
 * Demo audio URLs for testing purposes (using CORS-friendly sources)
 */
export const DEMO_AUDIO_URLS = {
  short: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav',
  // Using a working signed URL for demo
  medium:
    'https://storage.googleapis.com/ai-scribe-staging/merged-audio/merged-audio-6781f741-08e8-4b48-ad54-34832cb91c2a.wav?X-Goog-Algorithm=GOOG4-RSA-SHA256&X-Goog-Credential=ep3-staging%40gdb1-1261.iam.gserviceaccount.com%2F20250723%2Fauto%2Fstorage%2Fgoog4_request&X-Goog-Date=20250723T113907Z&X-Goog-Expires=54000&X-Goog-SignedHeaders=host&X-Goog-Signature=6ca07637c976b0dcfc7f63180bc79cb8c5637656b71ebf01f4707d21900998b349708f665178571d4ea29451bbec0df1cfafe2e39c99516e671cc952a2c9e2853634bb59a87c6f18ea2e5fe8a6670d64d8622edd7d2780d7601bd56c86017c64dec6632b941e167685816daf134041b0262c150e31db9ad2e98def01069d305a7c7dfd6b870351e6924db9ed173cbaa7730f163d1500cfc0dfaba460074c4ad861dc139108c12716bbe70368668cbcfff82af26aaee495456903873af556ba94a2c8982013c050f5ff9934fde3731c02beddd41399e30092e3fa0086e64fe144791f7ff2eda7dbf2e212a8bf7375ca43f1d1b6fc37f32fde19891a1accd1019b',
  // CORS-friendly alternative
  speech:
    'https://commondatastorage.googleapis.com/codeskulptor-demos/DDR_assets/Kangaroo_MusiQue_-_The_Neverwritten_Role_Playing_Game.mp3',
  // Local file for testing
  local: '/audio/sample.mp3',
} as const;

/**
 * Check if audio URL has CORS support
 * @param url - The audio URL to check
 * @returns Promise<boolean> - Whether the URL supports CORS
 */
export async function checkCorsSupport(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
    });
    return response.ok;
  } catch (error) {
    console.warn('CORS check failed for:', url, error);
    return false;
  }
}

/**
 * Get audio URL with CORS fallback strategy
 * @param audioFileKey - The audio file key from the job
 * @param options - Configuration options
 * @returns Promise<string> - The best available audio URL
 */
export async function getAudioUrlWithFallback(
  audioFileKey?: string,
  options: {
    useDemoInDevelopment?: boolean;
    useProxy?: boolean;
    enableCorsCheck?: boolean;
  } = {}
): Promise<string> {
  const {
    useDemoInDevelopment = true,
    useProxy = false,
    enableCorsCheck = false,
  } = options;

  // In development without real audio, use demo
  if (useDemoInDevelopment && import.meta.env.DEV && !audioFileKey) {
    return DEMO_AUDIO_URLS.speech; // Use CORS-friendly URL
  }

  if (!audioFileKey) return '';

  // Generate URLs to try
  const urls = [
    useProxy ? generateProxyAudioUrl(audioFileKey) : null,
    generateGcsAudioUrl(audioFileKey),
  ].filter(Boolean) as string[];

  // If CORS checking is disabled, return the first URL
  if (!enableCorsCheck) {
    return urls[0];
  }

  // Try each URL and return the first one that works
  for (const url of urls) {
    if (await checkCorsSupport(url)) {
      return url;
    }
  }

  // Fallback to demo audio if all else fails
  return DEMO_AUDIO_URLS.speech;
}

/**
 * Get the appropriate audio URL for the current environment (synchronous version)
 * @param audioFileKey - The audio file key from the job
 * @param useDemoInDevelopment - Whether to use demo audio in development
 * @returns The audio URL to use
 */
export function getAudioUrl(
  audioFileKey?: string,
  useDemoInDevelopment: boolean = true
): string {
  // In development, use demo audio if no real audio is available
  if (useDemoInDevelopment && import.meta.env.DEV && !audioFileKey) {
    return DEMO_AUDIO_URLS.speech; // Use CORS-friendly demo audio
  }

  // Return the real audio URL if available
  if (audioFileKey) {
    return generateGcsAudioUrl(audioFileKey);
  }

  // Fallback to empty string
  return '';
}

/**
 * Configure CORS for Google Cloud Storage bucket (instructions)
 * To fix CORS issues, you need to configure your GCS bucket with this CORS policy:
 *
 * ```json
 * [
 *   {
 *     "origin": ["http://localhost:8001", "http://localhost:3000", "https://yourdomain.com"],
 *     "method": ["GET", "HEAD"],
 *     "responseHeader": ["Content-Type", "Range", "Content-Range", "Content-Length"],
 *     "maxAgeSeconds": 3600
 *   }
 * ]
 * ```
 *
 * Apply with: `gsutil cors set cors.json gs://your-bucket-name`
 */
